import * as React from "react";
import { toast } from "react-toastify";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { gettext } from "i18n";
import { formatValueForTable } from "utils/formatting";
import { FullDataSourceLoader, useCombinedSourceData } from "utils/datacharts";
import {
  buildManualLabClipboardText,
  formatDateKey,
  formatParamTitle,
  processManualLabData,
} from "./manualLabData";

interface ManualLabDataTableProps {
  title?: string;
  startDate: Date;
  endDate: Date;
  sourceUUIDs: string[];
  layoutOption?: string;
}

const ManualLabDataTable: React.FC<ManualLabDataTableProps> = ({
  title,
  startDate,
  endDate,
  sourceUUIDs,
  layoutOption = "station_params_rows",
}) => {
  // Only the configured sources, also right after one is removed from the
  // widget settings.
  const [configuredDatapoints, setSourceData] = useCombinedSourceData(
    sourceUUIDs,
    startDate,
    endDate,
  );

  const processedData = React.useMemo(
    () => processManualLabData(configuredDatapoints),
    [configuredDatapoints],
  );

  const copyToClipboard = React.useCallback(() => {
    const tableContents = buildManualLabClipboardText(
      processedData,
      layoutOption,
    );

    const onError = (err: unknown) => {
      console.error(
        "Failed to copy to clipboard: ",
        err,
        `\n\nValue:\n${tableContents}`,
      );
      toast.error(gettext("Action failed"));
    };

    // writeText is async and rejects e.g. without clipboard permission;
    // navigator.clipboard is missing altogether outside secure contexts.
    try {
      navigator.clipboard
        .writeText(tableContents)
        .then(() => toast.success(gettext("Value copied to clipboard")))
        .catch(onError);
    } catch (err) {
      onError(err);
    }
  }, [processedData, layoutOption]);

  const renderCell = React.useCallback(
    (average: number | undefined, count: number | undefined) => {
      if (average === undefined) {
        return <span className="no-data-placeholder">-</span>;
      }

      return (
        <div className="cell-with-count">
          <strong>{formatValueForTable(average)}</strong>
          {count && count > 1 && (
            <span className="value-count-badge">{count}</span>
          )}
        </div>
      );
    },
    [],
  );

  const renderTable = () => {
    switch (layoutOption) {
      case "station_dates_rows": {
        const singleStation = processedData.allStations.length === 1;
        const stationName = singleStation
          ? processedData.allStations[0]?.name
          : null;
        return (
          <>
            {singleStation && stationName && (
              <h6 className="station-title">{stationName}</h6>
            )}
            <Table
              striped
              bordered
              hover
              responsive
              className="manual-lab-data-table"
            >
              <thead>
                <tr>
                  <th>{gettext("Date")}</th>
                  {processedData.sourceRows.map((row, sourceIdx) => (
                    <th key={sourceIdx}>
                      {singleStation ? (
                        <>
                          {row.sourceName} {row.unit ? `[${row.unit}]` : ""}
                        </>
                      ) : (
                        <>
                          <strong>{row.stationName}</strong>
                          <br />
                          {row.sourceName} {row.unit ? `[${row.unit}]` : ""}
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {processedData.dateColumns.map((dateKey) => (
                  <tr key={dateKey}>
                    <td>{formatDateKey(dateKey)}</td>
                    {processedData.sourceRows.map((row, sourceIdx) => (
                      <td key={`${dateKey}_${sourceIdx}`}>
                        {renderCell(
                          row.dailyAverages[dateKey],
                          row.dailyCounts[dateKey],
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        );
      }
      case "param_dates_rows": {
        return (
          <>
            {processedData.allParams.map((param) => (
              <div key={param.key} className="mb-4">
                <h6 className="param-title">
                  {formatParamTitle(param.name, param.unit)}
                </h6>
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  className="manual-lab-data-table"
                >
                  <thead>
                    <tr>
                      <th>{gettext("Date")}</th>
                      {processedData.allStations.map((station) => (
                        <th key={station.key}>{station.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {processedData
                      .getParamDateColumns(param.key)
                      .map((dateKey) => (
                        <tr key={dateKey}>
                          <td>{formatDateKey(dateKey)}</td>
                          {processedData.allStations.map((station) => {
                            const cell = processedData.getCell(
                              param.key,
                              station.key,
                            );
                            return (
                              <td
                                key={`${param.key}_${station.key}_${dateKey}`}
                              >
                                {renderCell(
                                  cell?.dailyAverages[dateKey],
                                  cell?.dailyCounts[dateKey],
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            ))}
          </>
        );
      }
      case "param_stations_rows": {
        return (
          <>
            {processedData.allParams.map((param) => {
              const dateColumns = processedData.getParamDateColumns(param.key);
              return (
                <div key={param.key} className="mb-4">
                  <h6 className="param-title">
                    {formatParamTitle(param.name, param.unit)}
                  </h6>
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                    className="manual-lab-data-table"
                  >
                    <thead>
                      <tr>
                        <th>{gettext("Station")}</th>
                        {dateColumns.map((dateKey) => (
                          <th key={dateKey}>{formatDateKey(dateKey)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {processedData.allStations.map((station) => {
                        const cell = processedData.getCell(
                          param.key,
                          station.key,
                        );
                        return (
                          <tr key={station.key}>
                            <td>{station.name}</td>
                            {dateColumns.map((dateKey) => (
                              <td
                                key={`${param.key}_${station.key}_${dateKey}`}
                              >
                                {renderCell(
                                  cell?.dailyAverages[dateKey],
                                  cell?.dailyCounts[dateKey],
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              );
            })}
          </>
        );
      }
      case "station_params_rows":
      default: {
        const singleStation = processedData.allStations.length === 1;
        const stationName = singleStation
          ? processedData.allStations[0]?.name
          : null;
        return (
          <>
            {singleStation && stationName && (
              <h6 className="station-title">{stationName}</h6>
            )}
            <Table
              striped
              bordered
              hover
              responsive
              className="manual-lab-data-table"
            >
              <thead>
                <tr>
                  <th>{gettext("Source")}</th>
                  {processedData.dateColumns.map((dateKey) => (
                    <th key={dateKey}>{formatDateKey(dateKey)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {processedData.sourceRows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    <td>
                      {singleStation ? (
                        <>
                          {row.sourceName} {row.unit ? `[${row.unit}]` : ""}
                        </>
                      ) : (
                        <>
                          <strong>{row.stationName}</strong>
                          <br />
                          {row.sourceName} {row.unit ? `[${row.unit}]` : ""}
                        </>
                      )}
                    </td>
                    {processedData.dateColumns.map((dateKey) => (
                      <td key={`${rowIdx}_${dateKey}`}>
                        {renderCell(
                          row.dailyAverages[dateKey],
                          row.dailyCounts[dateKey],
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        );
      }
    }
  };

  return (
    <Card className="ManualLabDataTable">
      {!!title && (
        <Card.Header>
          <h5>{gettext(title)}</h5>
        </Card.Header>
      )}
      <Card.Body>
        {sourceUUIDs?.map((sourceUUID, idx) => (
          <FullDataSourceLoader
            key={`additional_loader_${sourceUUID}_${idx}`}
            sourceUUID={sourceUUID}
            startDate={startDate}
            endDate={endDate}
            additionalDatapoints={configuredDatapoints}
            setSourceData={setSourceData}
          />
        ))}

        {processedData.sourceRows.length > 0 &&
          processedData.dateColumns.length > 0 && (
            <>
              <div className="mb-3">
                <span
                  className="copy-to-clipboard standalone"
                  onClick={copyToClipboard}
                >
                  <FontAwesomeIcon icon={faCopy} />
                  <span>copy</span>
                </span>
              </div>
              {renderTable()}
            </>
          )}

        {!sourceUUIDs?.length && (
          <div className="text-center py-4">
            <p>No data sources</p>
          </div>
        )}

        {!!sourceUUIDs?.length &&
          processedData.sourceRows.length === 0 &&
          configuredDatapoints.length === 0 && (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading data...</p>
            </div>
          )}

        {(processedData.sourceRows.length === 0 ||
          processedData.dateColumns.length === 0) &&
          configuredDatapoints.length > 0 && (
            <div className="text-center py-4">
              <p>No data available for the selected date range</p>
            </div>
          )}
      </Card.Body>
    </Card>
  );
};

export default ManualLabDataTable;
